import { useRef, useCallback, forwardRef, useImperativeHandle, memo, useState } from 'react'
import styled from 'styled-components'
import { Search, Close } from '@astronautsid/wpe-icons'
import { TextFieldBase } from '@astro-ui/components'
import { SidebarButtonIcon } from '../ButtonSideBar'

type SearchBarPropsType = {
  value: string
  onChange: (val: string) => void
  isSidebarCollapsed: boolean
  onExpandSidebar: () => void
}

export type SearchBarRefType = {
  focus: () => void
}

const SearchBar = forwardRef<SearchBarRefType, SearchBarPropsType>(
  ({ value, onChange, isSidebarCollapsed, onExpandSidebar }, ref) => {
    const inputRef = useRef<HTMLInputElement>(null)
    const [isEditable, setIsEditable] = useState(false)

    useImperativeHandle(ref, () => ({
      focus: () => inputRef.current?.focus(),
    }))

    const handleCollapsedIconClick = useCallback(() => {
      onExpandSidebar()
      // Wait for expand animation before focusing
      setTimeout(() => inputRef.current?.focus(), 300)
    }, [onExpandSidebar])

    const handleClear = useCallback(
      (e: React.MouseEvent) => {
        e.preventDefault()
        onChange('')
        inputRef.current?.focus()
      },
      [onChange],
    )

    if (isSidebarCollapsed) {
      return (
        <CollapsedSearchButton
          onClick={handleCollapsedIconClick}
          aria-label="Cari menu"
          type="button"
        >
          <SidebarButtonIcon>
            <Search />
          </SidebarButtonIcon>
        </CollapsedSearchButton>
      )
    }

    return (
      <SearchBarWrapper>
        <TextFieldBase
          fullWidth
          type="search"
          name="sidebar-menu-search"
          placeholder="Cari"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setIsEditable(true)}
          inputRef={inputRef}
          autoComplete="off"
          InputProps={{
            inputProps: { 'aria-label': 'Cari menu', readOnly: !isEditable },
            startAdornment: (
              <SearchIconAdornment>
                <Search />
              </SearchIconAdornment>
            ),
            endAdornment: value ? (
              <ClearButton onClick={handleClear} aria-label="Hapus pencarian" type="button">
                <Close />
              </ClearButton>
            ) : null,
          }}
        />
      </SearchBarWrapper>
    )
  },
)

SearchBar.displayName = 'SearchBar'

export default memo(SearchBar)

/* ─── Styled Components ─────────────────────────────────────────────────────── */

const SearchBarWrapper = styled.div`
  padding: 0 8px;

  /* Compact the TextFieldBase height to fit the sidebar */
  .MuiInputBase-root {
    padding: 7px 8px;
    min-height: unset;
  }

  .MuiInputBase-input {
    padding: 0;
    font-size: 14px;
    line-height: 22px;

    &::-webkit-search-cancel-button {
      display: none;
    }
  }
`

const SearchIconAdornment = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 4px;

  svg * {
    fill: ${({ theme }) => theme.palette.text.secondary};
  }
`

const ClearButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 2px;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  margin-left: 4px;

  &:hover {
    background: ${({ theme }) => theme.palette.action.hover};
  }

  svg * {
    fill: ${({ theme }) => theme.palette.text.secondary};
  }
`

const CollapsedSearchButton = styled.button`
  display: flex;
  padding: 10px 0;
  background: transparent;
  border: none;
  cursor: pointer;
  width: 100%;

  &:hover {
    svg * {
      fill: ${({ theme }) => theme.colors.mainV2};
    }
  }
`
